import { computed, unref, ref } from '@vue/composition-api'

export default (options = {}) => {
  // 1. オプション設定のデフォルト値
  const {
    showTertiaryColumns = false,
    includeActions = false, // actions列を含むか（固定列）
    includeDate = false, // 日付列を含むか
    useColumnWidths = false, // 列幅設定を使用するか
    useLabelIds = true, // true: expensePrimaryLabelId, false: expensePrimaryLabelName
    includeSupplierColumns = false, // 取引先・区分・控除対象額列（消費税概算）を含むか
    labels = null, // { primary, secondary, tertiary, quaternary } の各 ref（visible フィルタリング用）
  } = options

  // 2. 列幅設定の定数定義
  const COLUMN_WIDTHS = {
    ACTIONS: { maxWidth: '100px', minWidth: '100px', width: '100px' },
    DATE: { maxWidth: '120px', minWidth: '120px', width: '120px' },
    LABEL: { maxWidth: '250px', minWidth: '250px', width: '250px' },
    STANDARD: { maxWidth: '160px', minWidth: '160px', width: '160px' },
  }

  // 3. 固定列設定のヘルパー関数
  const createFixedColumn = config => ({
    ...config,
    class: 'fixed-cell',
    cellClass: 'fixed-cell',
  })

  // 4. 列幅を適用するヘルパー関数
  const applyWidth = (header, widthConfig) => {
    if (!useColumnWidths) {
      return header
    }

    return { ...header, ...widthConfig }
  }

  // 5. ヘッダー構築関数
  const buildExpenseHeaders = () => {
    const headers = []
    // showTertiaryColumns を reactive な値として取得（ref または computed の場合に対応）
    const shouldShowTertiary = unref(showTertiaryColumns)

    // actions列（固定列、オプション）
    if (includeActions) {
      const actionsHeader = {
        text: '',
        value: 'actions',
      }
      const actionsWithWidth = applyWidth(actionsHeader, COLUMN_WIDTHS.ACTIONS)
      headers.push(createFixedColumn(actionsWithWidth))
    }

    // 日付列（オプション）
    if (includeDate) {
      headers.push(
        applyWidth(
          {
            text: '日付',
            value: 'expenseDate',
          },
          COLUMN_WIDTHS.DATE,
        ),
      )
    }

    // 分類列のvalue名を決定
    const labelValuePrefix = useLabelIds
      ? 'expensePrimaryLabelId'
      : 'expensePrimaryLabelName'
    const secondaryValuePrefix = useLabelIds
      ? 'expenseSecondaryLabelId'
      : 'expenseSecondaryLabelName'
    const tertiaryValuePrefix = useLabelIds
      ? 'expenseTertiaryLabelId'
      : 'expenseTertiaryLabelName'
    const quaternaryValuePrefix = useLabelIds
      ? 'expenseQuaternaryLabelId'
      : 'expenseQuaternaryLabelName'

    // 主分類
    headers.push(
      applyWidth(
        {
          text: '主分類',
          value: labelValuePrefix,
        },
        COLUMN_WIDTHS.LABEL,
      ),
    )

    // 副分類
    headers.push(
      applyWidth(
        {
          text: '副分類',
          value: secondaryValuePrefix,
        },
        COLUMN_WIDTHS.LABEL,
      ),
    )

    // 条件付き分類列
    if (shouldShowTertiary) {
      headers.push(
        applyWidth(
          {
            text: '第三分類',
            value: tertiaryValuePrefix,
          },
          COLUMN_WIDTHS.LABEL,
        ),
      )

      headers.push(
        applyWidth(
          {
            text: '第四分類',
            value: quaternaryValuePrefix,
          },
          COLUMN_WIDTHS.LABEL,
        ),
      )
    }

    // その他の列
    headers.push(
      applyWidth(
        {
          text: '金額',
          value: 'amount',
        },
        COLUMN_WIDTHS.STANDARD,
      ),
    )

    // 税率列（ラベル名が異なる可能性がある）
    const taxRateText = useLabelIds ? '税率' : '消費税率'
    headers.push(
      applyWidth(
        {
          text: taxRateText,
          value: 'taxRate',
        },
        COLUMN_WIDTHS.STANDARD,
      ),
    )

    // 税額列
    const taxAmountText = useLabelIds ? '税額' : '消費税額'
    headers.push(
      applyWidth(
        {
          text: taxAmountText,
          value: 'taxAmount',
        },
        COLUMN_WIDTHS.STANDARD,
      ),
    )

    // 消費税（概算）関連列（オプション）
    if (includeSupplierColumns) {
      headers.push(
        applyWidth(
          {
            text: '取引先',
            value: 'expenseSupplierId',
          },
          COLUMN_WIDTHS.LABEL,
        ),
      )
      headers.push(
        applyWidth(
          {
            text: '区分',
            value: 'supplierInvoiceCategory',
          },
          COLUMN_WIDTHS.STANDARD,
        ),
      )
      headers.push(
        applyWidth(
          {
            text: '控除対象額',
            value: 'deductibleTaxAmount',
          },
          COLUMN_WIDTHS.STANDARD,
        ),
      )
    }

    // 非現金列（value名が異なる）
    const nonCashValue = useLabelIds ? 'meta.isNonCash' : 'isNonCash'
    headers.push(
      applyWidth(
        {
          text: '非現金',
          value: nonCashValue,
        },
        COLUMN_WIDTHS.STANDARD,
      ),
    )

    return headers
  }

  // 6. computed headers
  const headers = computed(() => {
    return buildExpenseHeaders()
  })

  // 7. visible フィルタリング済みラベル
  const _emptyRef = ref([])
  const primaryLabels = computed(() => (labels?.primary ?? _emptyRef).value.filter(label => label.attributes?.visible !== false))
  const secondaryLabels = computed(() => (labels?.secondary ?? _emptyRef).value.filter(label => label.attributes?.visible !== false))
  const tertiaryLabels = computed(() => (labels?.tertiary ?? _emptyRef).value.filter(label => label.attributes?.visible !== false))
  const quaternaryLabels = computed(() => (labels?.quaternary ?? _emptyRef).value.filter(label => label.attributes?.visible !== false))

  // 8. 階層(子ラベルキー → 親ラベルキー)。APIのラベル属性にも同名の親IDが入る
  const LABEL_HIERARCHY = {
    expenseSecondaryLabelId: 'expensePrimaryLabelId',
    expenseTertiaryLabelId: 'expenseSecondaryLabelId',
    expenseQuaternaryLabelId: 'expenseTertiaryLabelId',
  }

  const visibleLabelsByKey = {
    expensePrimaryLabelId: primaryLabels,
    expenseSecondaryLabelId: secondaryLabels,
    expenseTertiaryLabelId: tertiaryLabels,
    expenseQuaternaryLabelId: quaternaryLabels,
  }

  const rawLabelsByKey = {
    expensePrimaryLabelId: labels?.primary ?? _emptyRef,
    expenseSecondaryLabelId: labels?.secondary ?? _emptyRef,
    expenseTertiaryLabelId: labels?.tertiary ?? _emptyRef,
    expenseQuaternaryLabelId: labels?.quaternary ?? _emptyRef,
  }

  /**
   * 行の選択状態に応じた分類の選択肢。
   * 選択肢 = 親なし（共通ラベル） ∪ 選択中の親に紐づく子。親未選択なら共通ラベルのみ
   */
  const labelOptionsFor = (item, key) => {
    const labelList = visibleLabelsByKey[key]?.value ?? []
    const parentKey = LABEL_HIERARCHY[key]
    if (!parentKey) return labelList

    const parentId = item[parentKey]

    return labelList.filter(label => {
      const labelParentId = label.attributes?.[parentKey]
      if (!labelParentId) return true // 共通ラベル

      return Boolean(parentId) && Number(labelParentId) === Number(parentId)
    })
  }

  /**
   * deepest-wins税率解決: 選択パスの最も深い階層に設定されたdefaultTaxRateを返す。
   * どの階層にも設定がなければnull（自動セットなし＝手選択）
   */
  const resolveDefaultTaxRate = item => {
    const keys = [
      'expenseQuaternaryLabelId',
      'expenseTertiaryLabelId',
      'expenseSecondaryLabelId',
      'expensePrimaryLabelId',
    ]
    for (let i = 0; i < keys.length; i += 1) {
      const key = keys[i]
      const id = item[key]
      if (id) {
        const label = (rawLabelsByKey[key].value || []).find(l => Number(l.attributes?.id) === Number(id))
        const rate = label?.attributes?.defaultTaxRate
        if (rate) return rate
      }
    }

    return null
  }

  // 親を変更したときにクリア対象となりうる子孫のキー
  const descendantKeysOf = key => {
    const order = [
      'expensePrimaryLabelId',
      'expenseSecondaryLabelId',
      'expenseTertiaryLabelId',
      'expenseQuaternaryLabelId',
    ]
    const idx = order.indexOf(key)

    return idx >= 0 ? order.slice(idx + 1) : []
  }

  /**
   * 親の変更で実際に矛盾した子孫の選択のみクリアする。
   * 共通ラベル(親なし)はどの親の下でも有効なので選択を維持し、
   * 親付きラベルは選択中の親と一致しない場合のみクリアする(既存データの分類を勝手に消さない)
   */
  /* eslint-disable no-param-reassign */
  const clearInvalidDescendants = (item, key) => {
    descendantKeysOf(key).forEach(childKey => {
      const id = item[childKey]
      if (!id) return

      const label = (rawLabelsByKey[childKey].value || []).find(l => Number(l.attributes?.id) === Number(id))
      const parentKey = LABEL_HIERARCHY[childKey]
      const labelParentId = label?.attributes?.[parentKey]
      if (!labelParentId) return // 共通ラベル

      const selectedParentId = item[parentKey]
      if (!selectedParentId || Number(labelParentId) !== Number(selectedParentId)) {
        item[childKey] = null
      }
    })
  }
  /* eslint-enable no-param-reassign */

  return {
    headers,
    primaryLabels,
    secondaryLabels,
    tertiaryLabels,
    quaternaryLabels,
    labelOptionsFor,
    resolveDefaultTaxRate,
    descendantKeysOf,
    clearInvalidDescendants,
  }
}
